import * as React from "react";

interface FeaturesConsumerInterface {
  features: FeatureState[];
}

interface FeatureState {
  name: string;
  enabled?: boolean;
}

const defaultValue = {} as unknown as FeaturesConsumerInterface;

const FeaturesContext = React.createContext(defaultValue);

export interface FeatureController {
  name: string;
  read: () => FeatureState[];
}

interface Features_ControllerConsumerInterface {
  featureControllers: FeatureController[];
}

const defaultValue_controller =
  {} as unknown as Features_ControllerConsumerInterface;

const Features_ControllerContext = React.createContext(defaultValue_controller);

interface FeaturesProviderProps {
  features: FeatureController[];
}

export const FeaturesProvider: React.FunctionComponent<
  FeaturesProviderProps
> = (props) => {
  const features = React.useMemo(() => {
    const features = props.features
      .map((featureController) => {
        const featureState = featureController.read();
        return featureState;
      })
      .flat();
    return features;
  }, [props.features]);
  const value: FeaturesConsumerInterface = React.useMemo(
    () => ({
      features,
    }),
    [features]
  );
  const controllerValue: Features_ControllerConsumerInterface = React.useMemo(
    () => ({
      featureControllers: props.features,
    }),
    [props.features]
  );
  return (
    <FeaturesContext.Provider value={value}>
      <Features_ControllerContext.Provider value={controllerValue}>
        {props.children || []}
      </Features_ControllerContext.Provider>
    </FeaturesContext.Provider>
  );
};

export const useFeatures = (featureNames: string[]) => {
  const value = React.useContext(FeaturesContext);

  if (value === defaultValue) {
    throw new Error("useFeatures must be used within a FeaturesProvider");
  }

  const featureStates: (FeatureState | undefined)[] = React.useMemo(() => {
    return featureNames.map((featureName) => {
      const matchingFeature = value.features.find((feature) => {
        return feature.name === featureName;
      });
      return matchingFeature;
    });
  }, [value]);

  return featureStates;
};

export const useFeatures_asController = (featureNames: string[]) => {
  const value = React.useContext(Features_ControllerContext);

  if (value === defaultValue_controller) {
    throw new Error(
      "useFeatures_asController must be used within a FeaturesProvider"
    );
  }

  const featureControllers: (FeatureController | undefined)[] =
    React.useMemo(() => {
      return featureNames.map((featureName) => {
        const matchingFeatureController = value.featureControllers.find(
          (featureController) => {
            return featureController.name === featureName;
          }
        );
        return matchingFeatureController;
      });
    }, [value]);

  return featureControllers;
};
